"use client"
import { useState, useEffect } from "react"
import { useParams, Link } from "react-router-dom"
import { ChevronRight, CheckCircle, Phone, Award, Hash, Tag } from "lucide-react"
import { Helmet } from "react-helmet-async"
import "./ProductDetails.css"
import placeholder from "../../images/placeholder.png"

const ProductDetails = () => {
  const { id } = useParams()
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Fixed API URL to match Products component
  const API_BASE_URL = "https://imperia-backend.onrender.com/api/products"
  const BEARER_TOKEN = "YWRtaW5AZXhhbXBsZS5jb206c3VwZXJzZWNUR"

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${BEARER_TOKEN}`,
    "Content-Type": "application/json",
  })

  const processProductData = (apiProduct) => {
    return {
      id: apiProduct.productId,
      name: apiProduct.name || "Product Name",
      category: apiProduct.category || "General",
      price: apiProduct.price || 0,
      shortDescription: apiProduct.description || "Product description not available",
      images: apiProduct.imageUrl ? [apiProduct.imageUrl] : [placeholder],
      tags: apiProduct.tags || "", // Ensure tags are included here
      features: [
        "Engineered for High Performance",
        "Multi-Industry Compatibility",
        "Custom Build Solutions",
        "Long Term Durability",
        "Advance Construction & Material Science",
        "Seamless Integration with OEM & After-Market",
      ],
    }
  }

  // Format category for display
  const formatCategory = (category) => {
    return category.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
  }

  // Semantic tag grouping for better relevance
  const generateSemanticTagGroups = (product) => {
    const tagsArray = String(product.tags).split(",").filter(Boolean).map(tag => tag.trim())

    const materialTags = tagsArray.filter(tag =>
      tag.toLowerCase().includes('steel') ||
      tag.toLowerCase().includes('rubber') ||
      tag.toLowerCase().includes('plastic') ||
      tag.toLowerCase().includes('metal') ||
      tag.toLowerCase().includes('aluminum')
    )

    const applicationTags = tagsArray.filter(tag =>
      tag.toLowerCase().includes('industrial') ||
      tag.toLowerCase().includes('commercial') ||
      tag.toLowerCase().includes('residential') ||
      tag.toLowerCase().includes('automotive') ||
      tag.toLowerCase().includes('marine')
    )

    const featureTags = tagsArray.filter(tag =>
      tag.toLowerCase().includes('flexible') ||
      tag.toLowerCase().includes('durable') ||
      tag.toLowerCase().includes('pressure') ||
      tag.toLowerCase().includes('temperature') ||
      tag.toLowerCase().includes('resistant')
    )

    return { materialTags, applicationTags, featureTags, allTags: tagsArray }
  }

  // Optimized keyword generation with density control
  const generateOptimizedKeywords = (product) => {
    const { materialTags, applicationTags, featureTags, allTags } = generateSemanticTagGroups(product)

    // Primary tags (most important)
    const primaryTags = allTags.slice(0, 12)

    // Category variations
    const categoryKeywords = [
      formatCategory(product.category),
      `${formatCategory(product.category)} supplier`,
      `${formatCategory(product.category)} manufacturer`,
      `${formatCategory(product.category)} dealer`,
      `buy ${formatCategory(product.category)}`,
      `${formatCategory(product.category)} price`,
      `best ${formatCategory(product.category)}`,
      `quality ${formatCategory(product.category)}`,
      `professional ${formatCategory(product.category)}`
    ]

    // Product variations (limited)
    const productKeywords = [
      product.name,
      `${product.name} supplier`,
      `${product.name} manufacturer`,
      `buy ${product.name}`,
      `${product.name} price`,
      `best ${product.name}`,
      `${product.name} India`
    ]

    // Smart tag variations (avoid over-optimization)
    const tagKeywords = primaryTags.flatMap(tag => [
      tag,
      `${tag} supplier`,
      `buy ${tag}`,
      `${tag} price`,
      `best ${tag}`
    ])

    // Location keywords (selected cities)
    const locationKeywords = [
      `${product.name} India`,
      `${formatCategory(product.category)} India`,
      `${product.name} Delhi`,
      `${product.name} Mumbai`,
      `${product.name} Bangalore`,
      `${product.name} Pune`,
      `${product.name} Foreign`,
      `${product.name} USA`,
      `${product.name} Maharashtra`,
      `${product.name} Andhra Pradesh`,
      `${product.name} Chennai`
    ]

    return [...new Set([...primaryTags, ...categoryKeywords, ...productKeywords, ...tagKeywords.slice(0, 30), ...locationKeywords])].slice(0, 60).join(", ")
  }

  // Improved SEO title with better tag integration
  const generateOptimizedSEOTitle = (product) => {
    const { materialTags, featureTags, allTags } = generateSemanticTagGroups(product)

    // Use most relevant tags for title
    const titleTags = []
    if (materialTags.length > 0) titleTags.push(materialTags[0])
    if (featureTags.length > 0) titleTags.push(featureTags[0])
    if (titleTags.length < 3) {
      titleTags.push(...allTags.filter(tag => !titleTags.includes(tag)).slice(0, 3 - titleTags.length))
    }

    const tagString = titleTags.slice(0, 3).join(" | ")
    return `${product.name} - ${formatCategory(product.category)} | ${tagString} | Best Quality | Imperia Hoses`
  }

  // Natural meta description with semantic tag integration
  const generateNaturalSEODescription = (product) => {
    const { materialTags, applicationTags, featureTags, allTags } = generateSemanticTagGroups(product)

    let description = `${product.shortDescription} `

    // Add material information naturally
    if (materialTags.length > 0) {
      description += `Made with ${materialTags.slice(0, 2).join(" and ")}. `
    }

    // Add feature information
    if (featureTags.length > 0) {
      description += `Features ${featureTags.slice(0, 2).join(" and ")} design. `
    }

    // Add application information
    if (applicationTags.length > 0) {
      description += `Perfect for ${applicationTags.slice(0, 2).join(" and ")} use. `
    } else {
      // Fallback to general tags
      const generalTags = allTags.filter(tag => !materialTags.includes(tag) && !featureTags.includes(tag)).slice(0, 3)
      if (generalTags.length > 0) {
        description += `Ideal for ${generalTags.join(", ")} applications. `
      }
    }

    description += `Get instant quotes for premium ${formatCategory(product.category)} solutions.`

    // Ensure description stays under 160 characters
    return description.length > 160 ? description.substring(0, 157) + "..." : description
  }

  // Optimized long-tail keywords with better combinations
  const generateSmartLongTailKeywords = (product) => {
    const { materialTags, applicationTags, featureTags, allTags } = generateSemanticTagGroups(product)
    const longTailKeywords = []

    // Material + Application combinations
    materialTags.slice(0, 3).forEach(material => {
      applicationTags.slice(0, 3).forEach(application => {
        longTailKeywords.push(`${material} ${application} ${formatCategory(product.category)}`)
        longTailKeywords.push(`buy ${material} ${application}`)
      })
    })

    // Feature + Category combinations
    featureTags.slice(0, 3).forEach(feature => {
      longTailKeywords.push(`${feature} ${formatCategory(product.category)} supplier`)
      longTailKeywords.push(`${feature} ${product.name}`)
    })

    // Product + Tag combinations (limited to prevent stuffing)
    allTags.slice(0, 8).forEach(tag => {
      longTailKeywords.push(`${product.name} with ${tag}`)
    })

    return longTailKeywords.slice(0, 25).join(", ")
  }

  // Enhanced structured data with semantic organization
  const generateEnhancedStructuredData = (product) => {
    const { materialTags, applicationTags, featureTags, allTags } = generateSemanticTagGroups(product)

    return {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": product.name,
      "description": product.shortDescription,
      "productID": product.id,
      "category": formatCategory(product.category),
      "image": product.images[0],
      "keywords": allTags.slice(0, 15).join(", "), // Limit keywords
      "brand": {
        "@type": "Brand",
        "name": "Imperia Hoses"
      },
      "manufacturer": {
        "@type": "Organization",
        "name": "Imperia Hoses"
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "priceSpecification": {
          "@type": "PriceSpecification",
          "priceCurrency": "INR",
          "price": product.price
        },
        "seller": {
          "@type": "Organization",
          "name": "Imperia Hoses, Delhi, India"
        }
      },
      "additionalProperty": [
        ...materialTags.map(tag => ({
          "@type": "PropertyValue",
          "name": "material",
          "value": tag
        })),
        ...featureTags.map(tag => ({
          "@type": "PropertyValue",
          "name": "feature",
          "value": tag
        })),
        ...applicationTags.map(tag => ({
          "@type": "PropertyValue",
          "name": "application",
          "value": tag
        }))
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.5",
        "reviewCount": "100"
      }
    }
  }

  // Smart FAQ generation based on tag categories
  const generateTagBasedFAQs = (product) => {
    const { materialTags, applicationTags, featureTags, allTags } = generateSemanticTagGroups(product)

    const faqs = []

    // Material-based FAQ
    if (materialTags.length > 0) {
      faqs.push({
        "@type": "Question",
        "name": `What materials are used in ${product.name}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `${product.name} is constructed using ${materialTags.slice(0, 3).join(", ")}. These materials ensure durability and performance for ${formatCategory(product.category)} applications.`
        }
      })
    }

    // Feature-based FAQ
    if (featureTags.length > 0) {
      faqs.push({
        "@type": "Question",
        "name": `What are the key features of ${product.name}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `Key features include ${featureTags.slice(0, 4).join(", ")}. These features make it ideal for demanding ${formatCategory(product.category)} applications.`
        }
      })
    }

    // Application-based FAQ
    if (applicationTags.length > 0) {
      faqs.push({
        "@type": "Question",
        "name": `Where can I use ${product.name}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `${product.name} is suitable for ${applicationTags.slice(0, 3).join(", ")} applications. It's designed to meet the requirements of various ${formatCategory(product.category)} environments.`
        }
      })
    } else {
      // General application FAQ
      faqs.push({
        "@type": "Question",
        "name": `What applications is ${product.name} suitable for?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `${product.name} is perfect for applications requiring ${allTags.slice(0, 5).join(", ")}. Contact us to discuss your specific ${formatCategory(product.category)} requirements.`
        }
      })
    }

    // Purchasing FAQ
    faqs.push({
      "@type": "Question",
      "name": `Where can I buy ${product.name}?`,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": `You can buy ${product.name} from Imperia Hoses. We are leading suppliers and manufacturers of ${formatCategory(product.category)} in India with nationwide delivery.`
      }
    })

    return faqs
  }

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await fetch(`${API_BASE_URL}/${id}`, {
          method: "GET",
          headers: getAuthHeaders(),
        })
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const result = await response.json()
        console.log("API Response:", result) // Debug log
        if (result.success && result.data) {
          const processedProduct = processProductData(result.data)
          console.log("Processed Product:", processedProduct) // Debug log
          setProduct(processedProduct)
        } else {
          throw new Error(result.message || "Product not found")
        }
      } catch (error) {
        console.error("Error fetching product:", error)
        setError(error.message)
      } finally {
        setLoading(false)
      }
    }
    if (id) {
      fetchProduct()
    }
  }, [id])

  if (loading) {
    return (
      <div className="product-details-page">
        <div className="product-details-loading">
          <div className="loading-spinner"></div>
          <p>Loading product details...</p>
        </div>
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="product-not-found">
        <h2>Product Not Found</h2>
        <p>{error || "The product you're looking for doesn't exist."}</p>
        <Link to="/products" className="btn btn-primary">
          Back to Products
        </Link>
      </div>
    )
  }

  const allTagsArray = String(product.tags)
    .split(",")
    .filter(Boolean)
    .map((tag) => tag.trim())

  const { materialTags, applicationTags, featureTags } = generateSemanticTagGroups(product)

  return (
    <>
      {/* Optimized SEO Meta Tags for Maximum Google Visibility */}
      <Helmet>
        {/* Optimized Primary Title */}
        <title>{generateOptimizedSEOTitle(product)}</title>

        {/* Natural Meta Description */}
        <meta name="description" content={generateNaturalSEODescription(product)} />

        {/* Optimized Keywords - Density Controlled */}
        <meta name="keywords" content={generateOptimizedKeywords(product)} />

        {/* Smart Long-tail Keywords */}
        <meta name="long-tail-keywords" content={generateSmartLongTailKeywords(product)} />

        {/* Canonical URL */}
        <link rel="canonical" href={`${window.location.origin}/products/${id}`} />

        {/* Enhanced Open Graph Tags */}
        <meta property="og:type" content="product" />
        <meta property="og:title" content={generateOptimizedSEOTitle(product)} />
        <meta property="og:description" content={generateNaturalSEODescription(product)} />
        <meta property="og:image" content={product.images[0]} />
        <meta property="og:image:width" content="800" />
        <meta property="og:image:height" content="600" />
        <meta property="og:image:alt" content={`${product.name} - ${formatCategory(product.category)}`} />
        <meta property="og:url" content={`${window.location.origin}/products/${id}`} />
        <meta property="og:site_name" content="Imperia Hoses" />
        <meta property="og:locale" content="en_IN" />

        {/* Enhanced Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@ImperiaHoses" />
        <meta name="twitter:creator" content="@ImperiaHoses" />
        <meta name="twitter:title" content={generateOptimizedSEOTitle(product)} />
        <meta name="twitter:description" content={generateNaturalSEODescription(product)} />
        <meta name="twitter:image" content={product.images[0]} />
        <meta name="twitter:image:alt" content={`${product.name} - ${formatCategory(product.category)}`} />

        {/* Product Specific Meta Tags */}
        <meta name="product:brand" content="Imperia Hoses" />
        <meta name="product:availability" content="in stock" />
        <meta name="product:condition" content="new" />
        <meta name="product:price:amount" content={product.price} />
        <meta name="product:price:currency" content="INR" />
        <meta name="product:category" content={formatCategory(product.category)} />
        <meta name="product:retailer_item_id" content={product.id} />

        {/* Optimized SEO Tags */}
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="bingbot" content="index, follow" />
        <meta name="author" content="Imperia Hoses" />
        <meta name="publisher" content="Imperia Hoses" />
        <meta name="copyright" content="Imperia Hoses" />

        {/* Geographic and Local SEO */}
        <meta name="geo.region" content="IN" />
        <meta name="geo.placename" content="India" />
        <meta name="geo.position" content="28.6139;77.2090" />
        <meta name="ICBM" content="28.6139, 77.2090" />
        <meta name="DC.title" content={product.name} />

        {/* Industry and Business Meta Tags */}
        <meta name="industry" content={formatCategory(product.category)} />
        <meta name="business-type" content="manufacturer, supplier, dealer" />
        <meta name="target-audience" content="business, industrial, commercial, residential" />

        {/* Semantic Tag Categories - Material Tags */}
        {materialTags.slice(0, 5).map((tag, index) => [
          <meta key={`material-${index}`} name={`product-material-${index + 1}`} content={tag} />,
          <meta key={`material-search-${index}`} name={`material-keyword-${index + 1}`} content={`${tag} ${formatCategory(product.category)}`} />
        ]).flat()}

        {/* Semantic Tag Categories - Feature Tags */}
        {featureTags.slice(0, 5).map((tag, index) => [
          <meta key={`feature-${index}`} name={`product-feature-${index + 1}`} content={tag} />,
          <meta key={`feature-search-${index}`} name={`feature-keyword-${index + 1}`} content={`${tag} ${formatCategory(product.category)}`} />
        ]).flat()}

        {/* Semantic Tag Categories - Application Tags */}
        {applicationTags.slice(0, 5).map((tag, index) => [
          <meta key={`application-${index}`} name={`product-application-${index + 1}`} content={tag} />,
          <meta key={`application-search-${index}`} name={`application-keyword-${index + 1}`} content={`${tag} ${formatCategory(product.category)}`} />
        ]).flat()}

        {/* Top Priority Tags - Limited to prevent over-optimization */}
        {allTagsArray.slice(0, 10).map((tag, index) => [
          <meta key={`priority-tag-${index}`} name={`priority-feature-${index + 1}`} content={tag} />,
          <meta key={`priority-search-${index}`} name={`priority-keyword-${index + 1}`} content={`${tag} supplier India`} />
        ]).flat()}

        {/* Location Combinations - Key Cities Only */}
        {['Delhi', 'Mumbai', 'Bangalore', 'Chennai', 'Pune'].map((city, index) => (
          <meta key={`location-${index}`} name={`location-${city.toLowerCase()}`} content={`${product.name} ${city}, ${formatCategory(product.category)} ${city}`} />
        ))}

        {/* Enhanced Structured Data for Rich Snippets */}
        <script type="application/ld+json">
          {JSON.stringify(generateEnhancedStructuredData(product))}
        </script>

        {/* Enhanced Breadcrumb Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": window.location.origin
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Products & Solutions",
                "item": `${window.location.origin}/products`
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": formatCategory(product.category),
                "item": `${window.location.origin}/products?category=${product.category}`
              },
              {
                "@type": "ListItem",
                "position": 4,
                "name": product.name,
                "item": `${window.location.origin}/products/${id}`
              }
            ]
          })}
        </script>

        {/* Enhanced FAQ Structured Data with Semantic Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": generateTagBasedFAQs(product)
          })}
        </script>

        {/* Local Business Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Imperia Hoses",
            "image": product.images[0],
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "IN",
              "addressRegion": "Delhi",
              "addressLocality": "New Delhi"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 28.6139,
              "longitude": 77.2090
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": `${formatCategory(product.category)} Products`,
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Product",
                    "name": product.name,
                    "description": product.shortDescription,
                    "category": formatCategory(product.category)
                  }
                }
              ]
            }
          })}
        </script>

        {/* Organization Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Imperia Hoses",
            "description": `Leading manufacturer and supplier of ${formatCategory(product.category)} in India`,
            "url": window.location.origin,
            "logo": `${window.location.origin}/logo.png`,
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+91-9350019570",
              "contactType": "sales",
              "availableLanguage": ["English", "Hindi"]
            },
            "sameAs": [
              "https://www.linkedin.com/company/imperia-industries-india-private-limited/",
              "https://www.facebook.com/Hydpipe"
            ]
          })}
        </script>
      </Helmet>

      {/* Original UI Code - Completely Unchanged */}
      <div className="product-details-page">
        <div className="breadcrumb-section">
          <div className="container">
            <nav className="breadcrumb">
              <Link to="/">Home</Link>
              <ChevronRight size={16} />
              <Link to="/products">Products & Solutions</Link>
              <ChevronRight size={16} />
              <Link to={`/products?category=${product.category}`} className="category-breadcrumb">
                {formatCategory(product.category)}
              </Link>
              <ChevronRight size={16} />
              <span>{product.name}</span>
            </nav>
          </div>
        </div>
        <section className="product-hero">
          <div className="container">
            <div className="hero-content">
              <div className="product-gallery">
                <div className="main-image">
                  <img
                    src={product.images[0] || placeholder}
                    alt={product.name}
                    onError={(e) => {
                      e.target.src = placeholder
                      e.target.onerror = null // Prevent infinite loop if placeholder fails
                    }}
                  />
                </div>
              </div>
              <div className="product-info">
                <div className="product-header">
                  <div className="product-meta">
                    <div className="product-id-display">
                      <span>ID: {product.id}</span>
                    </div>
                    <div className="category-badge">
                      <Tag size={14} />
                      <span>{formatCategory(product.category)}</span>
                    </div>
                  </div>
                  <h1 className="product-title">{product.name}</h1>
                  <p className="product-description">{product.shortDescription}</p>
                </div>
                <div className="key-features">
                  <h3>Product Highlights</h3>
                  <div className="features-grid">
                    {product.features.slice(0, 6).map((feature, index) => (
                      <div key={index} className="feature-item">
                        <CheckCircle size={16} />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="product-actions">
                  <Link to="/contact" className="btn btn-primary">
                    <Phone size={18} />
                    Request Quote
                  </Link>
                  <Link to="/products" className="btn btn-secondary">
                    Back to Products
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* All tags section - Original Code */}
        {allTagsArray.length > 0 && (
          <section className="product-all-tags-section">
            <div className="container">
              <div className="tags-content">
                <span className="tags-label">All Tags:</span>
                <span className="tags-list">{allTagsArray.join(", ")}</span>
              </div>
            </div>
          </section>
        )}

        <section className="product-cta">
          <div className="container">
            <div className="cta-content">
              <Award size={48} />
              <h2>Need Custom Solutions?</h2>
              <p>Our engineering team can develop tailored solutions for your specific requirements.</p>
              <div className="cta-actions">
                <Link to="/contact" className="btn btn-primary">
                  Contact Our Experts
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default ProductDetails